import { Capacitor } from '@capacitor/core';
import { initializeApp } from 'firebase/app';
import { getMessaging, getToken } from 'firebase/messaging';
import { useEffect } from 'react';

import {
  useCreateNotificationMethodMutation,
  useNotificationMethodsQuery,
} from '@/store/notification-settings/notification-settings.store';
import { NotificationMethodType } from '@/store/notification-settings/notification-settings.types';

import { getDeviceInfo } from './device-info';

const firebaseConfig = {
  apiKey: import.meta.env.VITE_APP_FIREBASE_API_KEY,
  authDomain: import.meta.env.VITE_APP_FIREBASE_AUTH_DOMAIN,
  projectId: import.meta.env.VITE_APP_FIREBASE_PROJECT_ID,
  storageBucket: import.meta.env.VITE_APP_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: import.meta.env.VITE_APP_FIREBASE_MESSAGING_SENDER_ID,
  appId: import.meta.env.VITE_APP_FIREBASE_APP_ID,
  measurementId: import.meta.env.VITE_APP_FIREBASE_MEASUREMENT_ID,
};

const app = initializeApp(firebaseConfig);

const messaging =
  !Capacitor.isNativePlatform() && 'serviceWorker' in navigator ? getMessaging(app) : null;

const requestFcmToken = async () => {
  if (!messaging) return null;
  try {
    const permission = await Notification.requestPermission();
    if (permission !== 'granted') {
      return null;
    }
    const token = await getToken(messaging, {
      vapidKey: import.meta.env.VITE_APP_FIREBASE_VAPID_PUBLIC_KEY,
    });
    return token;
  } catch (err) {
    console.error('Error getting token:', err);
    return null;
  }
};

export const FirebaseProvider = () => {
  const { mutateAsync } = useCreateNotificationMethodMutation();
  const { data: methods } = useNotificationMethodsQuery();

  useEffect(() => {
    requestFcmToken().then(async (token) => {
      if (!token || !methods) return;
      const method = methods.find(
        ({ data, type }) => type === NotificationMethodType.PUSH && data === token,
      );

      if (!method) {
        const { deviceId, deviceName } = await getDeviceInfo();
        mutateAsync({ type: NotificationMethodType.PUSH, data: token, deviceId, deviceName });
      }
    });
  }, [mutateAsync, methods]);

  return null;
};
